import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import seloSystems from "../public/selo-systems.png";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Field, Input, Tag, Modal, brl, dataBR } from "../components/ui";
import AdminPerfis from "../components/admin/AdminPerfis";
import AdminAssinaturas from "../components/admin/AdminAssinaturas";
import AdminEmails from "../components/admin/AdminEmails";
import AdminComunicados from "../components/admin/AdminComunicados";
import AdminAuditoria from "../components/admin/AdminAuditoria";
import AdminConfig from "../components/admin/AdminConfig";

const PAPEL_LABEL = { proprietario: "Proprietário", associado: "Associado", estagiario: "Estagiário" };

const ABAS = [
    ["visao", "Visão geral"], ["escritorios", "Empresas"], ["assinaturas", "Assinaturas"],
    ["emails", "E-mails"], ["comunicados", "Comunicados"], ["auditoria", "Auditoria"], ["config", "Configurações"]
];

const NOVO_ESC_VAZIO = {
    nome_escritorio: "", uf: "", cidade: "",
    nome: "", email: "", senha: "", telefone: "", oab: "", uf_oab: ""
};

/** Painel do administrador do sistema — visão de todos os escritórios */
const Admin = () => {
    const navigate = useNavigate();
    const { admin, logout } = useAuth();
    const [aba, setAba] = useState("visao");
    const [subEmp, setSubEmp] = useState("escritorios"); // Empresas: escritórios | perfis
    const [resumo, setResumo] = useState(null);
    const [escritorios, setEscritorios] = useState([]);
    const [busca, setBusca] = useState("");
    const [detalhe, setDetalhe] = useState(null);
    const [erro, setErro] = useState(null);
    const [mensagem, setMensagem] = useState(null);
    // Criar escritório + proprietário
    const [mostrarNovo, setMostrarNovo] = useState(false);
    const [novoEsc, setNovoEsc] = useState(NOVO_ESC_VAZIO);
    const [criando, setCriando] = useState(false);
    // Redefinir senha de um login
    const [resetUsuario, setResetUsuario] = useState(null);
    const [novaSenha, setNovaSenha] = useState("");
    // Mensalidade do escritório (assinatura SaaS)
    const [mensalidade, setMensalidade] = useState("");
    const [vencimento, setVencimento] = useState("");

    const carregar = useCallback(async () => {
        try {
            const [r, e] = await Promise.all([api.get("/admin/resumo"), api.get("/admin/escritorios")]);
            setResumo(r.data);
            setEscritorios(e.data);
        } catch {
            setErro("Não foi possível carregar os dados.");
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const avisar = (texto) => {
        setMensagem(texto);
        setErro(null);
        setTimeout(() => setMensagem(null), 5000);
    };

    const abrirDetalhe = async (esc) => {
        try {
            const { data } = await api.get(`/admin/escritorios/${esc.id_escritorio}`);
            setDetalhe(data);
        } catch {
            alert("Não foi possível carregar o escritório.");
        }
    };

    const alternarSituacao = async (esc, e) => {
        e?.stopPropagation();
        const acao = esc.ativo ? "desativar" : "ativar";
        if (!window.confirm(`Deseja ${acao} o escritório ${esc.nome}?`)) return;
        try {
            await api.patch(`/admin/escritorios/${esc.id_escritorio}`, { ativo: !esc.ativo });
            carregar();
            if (detalhe?.escritorio.id_escritorio === esc.id_escritorio) {
                setDetalhe((d) => ({ ...d, escritorio: { ...d.escritorio, ativo: !esc.ativo } }));
            }
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível alterar a situação.");
        }
    };

    const recarregarDetalhe = async (id) => {
        try {
            const { data } = await api.get(`/admin/escritorios/${id}`);
            setDetalhe(data);
        } catch { /* mantém o que estava */ }
    };

    // Sincroniza os campos de mensalidade/vencimento quando abre/atualiza o detalhe
    useEffect(() => {
        setMensalidade(detalhe?.assinatura?.valor_mensal ?? "");
        setVencimento(detalhe?.assinatura?.data_vencimento ?? "");
    }, [detalhe]);

    const salvarVencimento = async () => {
        const id = detalhe.escritorio.id_escritorio;
        try {
            await api.put(`/admin/escritorios/${id}/vencimento`, { data: vencimento });
            avisar("Vencimento atualizado.");
            recarregarDetalhe(id);
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível salvar o vencimento.");
        }
    };

    const salvarMensalidade = async () => {
        const id = detalhe.escritorio.id_escritorio;
        try {
            await api.put(`/admin/escritorios/${id}/mensalidade`, { valor: Number(mensalidade) });
            avisar("Mensalidade definida.");
            recarregarDetalhe(id);
            carregar();
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível salvar a mensalidade.");
        }
    };

    const renovarAssinatura = async () => {
        const id = detalhe.escritorio.id_escritorio;
        if (!window.confirm("Renovar a assinatura por mais 1 mês SEM cobrança (renovação manual)?")) return;
        try {
            await api.post(`/admin/escritorios/${id}/renovar`);
            avisar("Assinatura renovada por mais 1 mês.");
            recarregarDetalhe(id);
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível renovar.");
        }
    };

    const mudarNovo = (campo, valor) => setNovoEsc((n) => ({ ...n, [campo]: valor }));

    const criarEscritorio = async (e) => {
        e.preventDefault();
        setCriando(true);
        setErro(null);
        try {
            const { data } = await api.post("/admin/escritorios", novoEsc);
            avisar(data.message);
            setMostrarNovo(false);
            setNovoEsc(NOVO_ESC_VAZIO);
            carregar();
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível criar o escritório.");
        } finally {
            setCriando(false);
        }
    };

    const alternarSituacaoUsuario = async (u) => {
        const acao = u.ativo ? "desativar" : "ativar";
        if (!window.confirm(`Deseja ${acao} o login de ${u.nome}?`)) return;
        try {
            await api.patch(`/admin/usuarios/${u.id_usuario}`, { ativo: !u.ativo });
            recarregarDetalhe(detalhe.escritorio.id_escritorio);
            carregar();
        } catch (err) {
            alert(err.response?.data?.error || "Não foi possível alterar o login.");
        }
    };

    const confirmarReset = async (e) => {
        e.preventDefault();
        setErro(null);
        try {
            const { data } = await api.put(`/admin/usuarios/${resetUsuario.id_usuario}/senha`, { nova_senha: novaSenha });
            avisar(data.message);
            setResetUsuario(null);
            setNovaSenha("");
        } catch (err) {
            setErro(err.response?.data?.error || "Não foi possível redefinir a senha.");
        }
    };

    const sair = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const filtrados = escritorios.filter((e) =>
        e.nome.toLowerCase().includes(busca.toLowerCase()) ||
        (e.proprietario || "").toLowerCase().includes(busca.toLowerCase()) ||
        (e.email_proprietario || "").toLowerCase().includes(busca.toLowerCase())
    );

    // Métricas com contexto: o número grande + uma linha de apoio
    const METRICAS = resumo ? [
        { rotulo: "Escritórios", valor: resumo.escritorios, apoio: `${resumo.escritorios_ativos} ativos · ${resumo.escritorios_novos} novos em 30 dias` },
        { rotulo: "Usuários", valor: resumo.usuarios, apoio: `${resumo.usuarios_ativos} ativos` },
        { rotulo: "Contratos", valor: resumo.contratos, apoio: `${resumo.contratos_ativos} em andamento` },
        { rotulo: "Valor sob gestão", valor: brl(resumo.valor_sob_gestao), apoio: "somando contratos não cancelados", destaque: true },
        { rotulo: "Clientes", valor: resumo.clientes, apoio: "cadastrados em todos os escritórios" },
        { rotulo: "Prazos vencidos", valor: resumo.prazos_vencidos, apoio: "pendentes com data passada", alerta: resumo.prazos_vencidos > 0 }
    ] : [];

    return (
        <div>
            <nav className="nav">
                <span className="nav-brand">
                    <img src={seloSystems} alt="Selo Systems" style={{ height: 40 }} />
                    Selo Systems
                </span>
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="text-muted" style={{ fontSize: 14.5 }}>{admin?.login} · Administrador</span>
                    <Button variante="ghost" onClick={sair}>Sair</Button>
                </div>
            </nav>

            <main className="page">
                <h2 style={{ margin: 0 }}>Painel do sistema</h2>

                {erro && <p className="erro">{erro}</p>}
                {mensagem && <p className="sucesso">{mensagem}</p>}

                <div className="nav-pilula" style={{ marginTop: 4, marginBottom: 12, flexWrap: "wrap" }}>
                    {ABAS.map(([k, r]) => (
                        <button key={k} type="button" className={aba === k ? "ativo" : ""} onClick={() => setAba(k)}>{r}</button>
                    ))}
                </div>

                {aba === "visao" && (
                    <div className="grid-cards">
                        {METRICAS.map((m) => (
                            <Card key={m.rotulo} style={m.alerta ? { borderLeft: "3px solid #a32b1d" } : undefined}>
                                <span className="card-kicker">{m.rotulo}</span>
                                <span style={{
                                    fontSize: m.destaque ? 24 : 30, fontFamily: "var(--font-heading)", fontWeight: 800,
                                    fontVariantNumeric: "tabular-nums",
                                    color: m.alerta ? "#a32b1d" : "var(--color-text)"
                                }}>
                                    {m.valor ?? "—"}
                                </span>
                                <span className="card-body text-muted">{m.apoio}</span>
                            </Card>
                        ))}
                    </div>
                )}

                {aba === "escritorios" && (
                    <>
                        <div className="toolbar">
                            <div className="nav-pilula">
                                <button type="button" className={subEmp === "escritorios" ? "ativo" : ""} onClick={() => setSubEmp("escritorios")}>Escritórios</button>
                                <button type="button" className={subEmp === "perfis" ? "ativo" : ""} onClick={() => setSubEmp("perfis")}>Perfis</button>
                            </div>
                            {subEmp === "escritorios" && (
                                <div className="toolbar-spacer">
                                    <Input placeholder="Buscar escritório" value={busca} onChange={(e) => setBusca(e.target.value)} />
                                    <Button onClick={() => { setNovoEsc(NOVO_ESC_VAZIO); setErro(null); setMostrarNovo(true); }}>Novo escritório</Button>
                                </div>
                            )}
                        </div>

                        {subEmp === "perfis" ? <AdminPerfis /> : (
                            <div className="scroll-x">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Escritório</th><th>Proprietário</th>
                                            <th className="num">Usuários</th><th className="num">Clientes</th><th className="num">Contratos</th>
                                            <th>Desde</th><th>Situação</th><th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtrados.map((e) => (
                                            <tr key={e.id_escritorio} className="clicavel" onClick={() => abrirDetalhe(e)}>
                                                <td>{e.nome}</td>
                                                <td>{e.proprietario || "—"}<br /><span className="text-muted" style={{ fontSize: 13.5 }}>{e.email_proprietario || ""}</span></td>
                                                <td className="num">{e.total_usuarios}</td>
                                                <td className="num">{e.total_clientes}</td>
                                                <td className="num">{e.total_contratos}</td>
                                                <td className="num">{dataBR(e.criado_em)}</td>
                                                <td><Tag tom={e.ativo ? "success" : "danger"}>{e.ativo ? "ativo" : "desativado"}</Tag></td>
                                                <td>
                                                    <Button variante={e.ativo ? "danger" : "ghost"} onClick={(ev) => alternarSituacao(e, ev)}>
                                                        {e.ativo ? "Desativar" : "Ativar"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {filtrados.length === 0 && (
                                            <tr><td colSpan="8" className="text-muted">Nenhum escritório encontrado.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {aba === "assinaturas" && <AdminAssinaturas />}
                {aba === "emails" && <AdminEmails avisar={avisar} />}
                {aba === "comunicados" && <AdminComunicados avisar={avisar} />}
                {aba === "auditoria" && <AdminAuditoria />}
                {aba === "config" && <AdminConfig avisar={avisar} />}

                {detalhe && (
                    <Modal titulo={detalhe.escritorio.nome} largo onFechar={() => setDetalhe(null)}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            <Tag tom={detalhe.escritorio.ativo ? "success" : "danger"}>
                                {detalhe.escritorio.ativo ? "ativo" : "desativado"}
                            </Tag>
                            <span className="text-muted" style={{ fontSize: 14.5 }}>
                                No sistema desde {dataBR(detalhe.escritorio.criado_em)}
                                {detalhe.escritorio.cidade && ` · ${detalhe.escritorio.cidade}/${detalhe.escritorio.UF || ""}`}
                            </span>
                        </div>

                        <div className="grid-cards" style={{ marginTop: 4 }}>
                            {[
                                ["Logins", `${detalhe.equipe.filter((u) => u.ativo).length}/${detalhe.equipe.length}`],
                                ["Clientes", detalhe.metricas.clientes],
                                ["Contratos", `${detalhe.metricas.contratos_ativos}/${detalhe.metricas.contratos}`],
                                ["Processos", detalhe.metricas.processos],
                                ["Compromissos", detalhe.metricas.compromissos],
                                ["Valor sob gestão", brl(detalhe.metricas.valor_sob_gestao)]
                            ].map(([rotulo, valor]) => (
                                <Card key={rotulo}>
                                    <span className="card-kicker">{rotulo}</span>
                                    <span style={{ fontSize: 20, fontFamily: "var(--font-heading)", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{valor}</span>
                                </Card>
                            ))}
                        </div>

                        <h4 style={{ margin: "8px 0 0" }}>Equipe · logins</h4>
                        <div className="scroll-x">
                            <table className="table">
                                <thead><tr><th>Nome</th><th>E-mail</th><th>Papel</th><th>OAB</th><th>Situação</th><th>Ações</th></tr></thead>
                                <tbody>
                                    {detalhe.equipe.map((u) => (
                                        <tr key={u.id_usuario}>
                                            <td>{u.nome}</td>
                                            <td>{u.email}</td>
                                            <td><Tag tom={u.papel === "proprietario" ? "accent" : "neutral"}>{PAPEL_LABEL[u.papel]}</Tag></td>
                                            <td className="num">{u.OAB ? `${u.OAB}${u.UF_OAB ? "/" + u.UF_OAB : ""}` : "—"}</td>
                                            <td><Tag tom={u.ativo ? "success" : "danger"}>{u.ativo ? "ativo" : "desativado"}</Tag></td>
                                            <td>
                                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                    <Button variante="secondary" onClick={() => { setResetUsuario(u); setNovaSenha(""); setErro(null); }}>
                                                        Redefinir senha
                                                    </Button>
                                                    <Button variante={u.ativo ? "danger" : "ghost"} onClick={() => alternarSituacaoUsuario(u)}>
                                                        {u.ativo ? "Desativar" : "Ativar"}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4 style={{ margin: "8px 0 0" }}>Mensalidade (SaaS)</h4>
                        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                            <Field label="Valor mensal (R$)">
                                <Input
                                    type="number" min="0" step="0.01" placeholder="Ex.: 99.90"
                                    value={mensalidade}
                                    onChange={(e) => setMensalidade(e.target.value)}
                                    style={{ maxWidth: 160 }}
                                />
                            </Field>
                            <Button onClick={salvarMensalidade} disabled={!mensalidade}>Salvar</Button>
                            {detalhe.assinatura && (
                                <>
                                    <Tag tom={detalhe.assinatura.vencida ? "danger" : detalhe.assinatura.status === "ativa" ? "success" : detalhe.assinatura.status === "pendente" ? "warn" : "danger"}>
                                        {detalhe.assinatura.vencida ? "vencida" : detalhe.assinatura.status === "pendente" ? "aguardando assinatura" : detalhe.assinatura.status}
                                    </Tag>
                                    <Button variante="secondary" onClick={renovarAssinatura}>Renovar +1 mês (sem cobrança)</Button>
                                </>
                            )}
                        </div>
                        {detalhe.assinatura && detalhe.assinatura.data_vencimento && (
                            <p className={detalhe.assinatura.vencida ? "" : "text-muted"} style={{ margin: "6px 0 0", fontSize: 13.5, color: detalhe.assinatura.vencida ? "#a32b1d" : undefined }}>
                                {detalhe.assinatura.vencida
                                    ? `Vencida em ${dataBR(detalhe.assinatura.data_vencimento)}`
                                    : `Vence em ${dataBR(detalhe.assinatura.data_vencimento)}${typeof detalhe.assinatura.dias_para_vencer === "number" ? ` (${detalhe.assinatura.dias_para_vencer} dia${detalhe.assinatura.dias_para_vencer === 1 ? "" : "s"})` : ""}`}
                            </p>
                        )}
                        {detalhe.assinatura && (
                            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 8 }}>
                                <Field label="Vencimento">
                                    <Input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} />
                                </Field>
                                <Button variante="secondary" onClick={salvarVencimento} disabled={!vencimento}>Salvar vencimento</Button>
                            </div>
                        )}
                        {detalhe.cobrancas && detalhe.cobrancas.length > 0 && (
                            <ul className="mini-lista" style={{ marginTop: 8 }}>
                                {detalhe.cobrancas.map((c) => (
                                    <li key={c.cod_cobranca}>
                                        <Tag tom={c.status === "approved" ? "success" : c.status === "pending" ? "warn" : "danger"}>{c.status}</Tag>
                                        {c.valor != null && <span>{brl(c.valor)}</span>}
                                        <span className="text-muted" style={{ marginLeft: "auto" }}>{dataBR(c.data_pagamento || c.criado_em)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <div className="modal-acoes">
                            <Button
                                variante={detalhe.escritorio.ativo ? "danger" : "primary"}
                                onClick={() => alternarSituacao({ id_escritorio: detalhe.escritorio.id_escritorio, nome: detalhe.escritorio.nome, ativo: detalhe.escritorio.ativo })}
                            >
                                {detalhe.escritorio.ativo ? "Desativar escritório" : "Ativar escritório"}
                            </Button>
                            <Button variante="secondary" onClick={() => setDetalhe(null)}>Fechar</Button>
                        </div>
                    </Modal>
                )}

                {mostrarNovo && (
                    <Modal titulo="Novo escritório" largo onFechar={() => setMostrarNovo(false)}>
                        <form onSubmit={criarEscritorio} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <h4 style={{ margin: 0 }}>Escritório</h4>
                            <div className="grid-form">
                                <Field label="Nome do escritório" className="span-2">
                                    <Input required value={novoEsc.nome_escritorio} onChange={(e) => mudarNovo("nome_escritorio", e.target.value)} />
                                </Field>
                                <Field label="Cidade">
                                    <Input value={novoEsc.cidade} onChange={(e) => mudarNovo("cidade", e.target.value)} />
                                </Field>
                                <Field label="UF">
                                    <Input maxLength={2} placeholder="SP" value={novoEsc.uf}
                                        onChange={(e) => mudarNovo("uf", e.target.value.toUpperCase())} />
                                </Field>
                            </div>

                            <h4 style={{ margin: "4px 0 0" }}>Proprietário (login inicial)</h4>
                            <div className="grid-form">
                                <Field label="Nome">
                                    <Input required value={novoEsc.nome} onChange={(e) => mudarNovo("nome", e.target.value)} />
                                </Field>
                                <Field label="E-mail">
                                    <Input type="email" required autoComplete="off" value={novoEsc.email} onChange={(e) => mudarNovo("email", e.target.value)} />
                                </Field>
                                <Field label="Senha (mín. 6 caracteres)">
                                    <Input type="password" required minLength={6} autoComplete="new-password"
                                        value={novoEsc.senha} onChange={(e) => mudarNovo("senha", e.target.value)} />
                                </Field>
                                <Field label="Telefone">
                                    <Input value={novoEsc.telefone} onChange={(e) => mudarNovo("telefone", e.target.value)} />
                                </Field>
                                <Field label="OAB (opcional)">
                                    <Input maxLength={6} placeholder="123456" value={novoEsc.oab}
                                        onChange={(e) => mudarNovo("oab", e.target.value.replace(/\D/g, ""))} />
                                </Field>
                                <Field label="UF da OAB">
                                    <Input maxLength={2} placeholder="SP" value={novoEsc.uf_oab}
                                        onChange={(e) => mudarNovo("uf_oab", e.target.value.toUpperCase())} />
                                </Field>
                            </div>

                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setMostrarNovo(false)}>Voltar</Button>
                                <Button type="submit" disabled={criando}>{criando ? "Criando..." : "Criar escritório"}</Button>
                            </div>
                        </form>
                    </Modal>
                )}

                {resetUsuario && (
                    <Modal titulo={`Redefinir senha — ${resetUsuario.nome}`} onFechar={() => setResetUsuario(null)}>
                        <form onSubmit={confirmarReset} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <p className="card-body text-muted" style={{ margin: 0 }}>
                                Define uma nova senha para o login <strong>{resetUsuario.email}</strong>.
                                Informe a nova senha ao usuário — ela substitui a atual imediatamente.
                            </p>
                            <Field label="Nova senha (mín. 6 caracteres)">
                                <Input type="password" required minLength={6} autoComplete="new-password"
                                    value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} />
                            </Field>
                            <div className="modal-acoes">
                                {erro && <p className="erro">{erro}</p>}
                                <Button variante="secondary" onClick={() => setResetUsuario(null)}>Voltar</Button>
                                <Button type="submit">Redefinir</Button>
                            </div>
                        </form>
                    </Modal>
                )}

            </main>
        </div>
    );
};

export default Admin;
